import React, { useState } from 'react';

import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle, TabContent, TabPane } from 'reactstrap';
import styled from 'styled-components';

import ContentContainer from 'components/ContentContainer/ContentContainer';
import TabFooter from 'components/ModularBlocks/TabTable/components/TabFooter';
import Table from 'components/ModularBlocks/TabTable/components/Table';

import { TabTableI } from 'interfaces/content-stack/modular-blocks/tabTable';

const StyledDropdownToggle = styled(DropdownToggle)`
  display: flex !important;
  align-items: center;
  border: none !important;
  border-radius: 0.25rem !important;
  color: ${(props) => props.theme.colors.white} !important;
  background-color: ${(props) => props.theme.colors.primaryRed} !important;
  font-family: ${(props) => props.theme.fonts.fontFamilySecondary} !important;
  font-size: 1rem;
  font-weight: 500;
  justify-content: space-between;
  width: 100%;
  padding: 0.75rem 1.5rem 0.75rem 1rem !important;
  text-transform: uppercase;

  &:hover,
  &:active,
  &:focus {
    background-color: ${(props) => props.theme.colors.primaryRed} !important;
    outline: none;
    box-shadow: none;
  }
`;

const StyledDropdownMenu = styled(DropdownMenu)`
  width: 100%;
  font-family: ${(props) => props.theme.fonts.default};
  padding: 0 !important;

  button {
    &:hover,
    &:active,
    &:focus {
      outline: none;
      box-shadow: none;
      background-color: transparent;
      color: black;
    }
  }
`;

const StyledDropdown = styled(Dropdown)`
  width: 100%;

  color: ${(props) => props.theme.colors.red};

  @media (min-width: ${(props) => props.theme.breakpoints.lg}) {
    display: none;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding: 0;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.xl}) {
    padding: 0;
  }
`;

const Spacer = styled.div`
  padding-top: 1rem;
`;

const StyledDropdownItem = styled(DropdownItem)`
  font-weight: 500 !important;
  padding: 0.625rem 1.25rem !important;
`;

const TabList = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 15.5rem;
  grid-row-gap: 0.5rem;
  background-color: ${(props) => props.theme.colors.secondaryGray5};
  flex-direction: column;
  margin-right: 3rem;
  padding: 0.5rem;

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    display: none;
  }
`;

const TabLink = styled.button`
  cursor: pointer;
  color: ${(props) => props.theme.colors.primaryBlack};
  background-color: transparent;
  transition:
    color 0.3s cubic-bezier(0.165, 0.84, 0.44, 1),
    background-color 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);

  display: flex;
  flex: 0 auto;
  justify-content: flex-start;
  padding: 1rem 1.5rem;

  border: none;
  border-radius: 0.25rem;
  align-items: center;

  text-align: center;
  text-decoration: none;
  line-height: 1.3;

  font-family: ${(props) => props.theme.fonts.default};
  font-size: 0.875rem;
  font-weight: 500;

  &:hover {
    background-color: ${(props) => props.theme.colors.white};
    color: ${(props) => props.theme.colors.primaryBlack2};
    font-weight: 600;
  }

  &&&.active {
    background-color: ${(props) => props.theme.colors.white};
    color: ${(props) => props.theme.colors.primaryBlack2};
    font-weight: 700;
  }
`;

const TabHeader = styled.h3`
  text-transform: uppercase;
  font-size: 1.5rem;
  font-family: ${(props) => props.theme.fonts.fontFamilySecondary};
  margin-top: 0.15rem;
  margin-bottom: 0.7rem;
  font-weight: 500;
  line-height: 1.2;

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    display: none;
  }
`;

const TabTableContainer = styled.div`
  @media (min-width: ${(props) => props.theme.breakpoints.lg}) {
    display: flex;
  }
`;

const StyledTabContent = styled(TabContent)`
  flex: 1;
`;

export interface TabTableProps {
  className: string;
  data: TabTableI;
}

function TabTable({ data, className }: TabTableProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const toggleTab = (tab: number) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const handleDropdownItemClick = (tab: number) => {
    toggleTab(tab);
    setDropdownOpen(true);
  };

  const toggleDropdown = () => setDropdownOpen((prevState) => !prevState);

  if (!data) {
    return null;
  }

  return (
    <ContentContainer>
      <TabTableContainer className={className}>
        <StyledDropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
          <StyledDropdownToggle>
            {data.tabs[activeTab].tabTitle}
            <FontAwesomeIcon size={'xs'} icon={faChevronDown} />
          </StyledDropdownToggle>
          <Spacer />
          <StyledDropdownMenu toggle={toggleDropdown}>
            {data.tabs?.map((tab, tabIndex) => (
              <StyledDropdownItem
                key={tabIndex}
                isActive={activeTab === tabIndex}
                onClick={() => handleDropdownItemClick(tabIndex)}
              >
                {tab.tabTitle}
              </StyledDropdownItem>
            ))}
          </StyledDropdownMenu>
        </StyledDropdown>
        <TabList role='tablist'>
          {data.tabs?.map((tab, tabIndex) => (
            <TabLink
              key={tabIndex}
              className={activeTab === tabIndex ? 'active' : ''}
              onClick={() => toggleTab(tabIndex)}
            >
              <div>{tab.tabTitle}</div>
            </TabLink>
          ))}
        </TabList>
        <StyledTabContent activeTab={activeTab}>
          {data.tabs?.map((tab, tabIndex) => (
            <TabPane key={tabIndex} tabId={tabIndex}>
              <TabHeader>{tab.tabTitle}</TabHeader>
              <Table data={tab} />
              <TabFooter tab={tab} />
            </TabPane>
          ))}
        </StyledTabContent>
      </TabTableContainer>
    </ContentContainer>
  );
}

export default React.memo(TabTable);
